import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React, {
  type CSSProperties,
  type PropsWithChildren,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router-dom';

import {useIsTextOverflowing} from '../../../util/hooks/useIsTextOverflowing';
import {TruncationType} from './TruncateTextTypes';

interface TruncateTextProps {
  fullText: string;
  className?: string;
  isDisabled?: boolean;
  placeholderType?: 'ellipsis' | 'doubleArrow' | 'expand';
  noLink?: boolean;
  linkPath?: string;
  truncationType: TruncationType;
  highlight?: (fullText: string) => React.ReactFragment;
  dataTest?: string;
  onClick?: React.MouseEventHandler;
  textAlign?: 'left' | 'right';
}

const truncateEndStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const truncateBeginningStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  direction: 'rtl',
  textAlign: 'left',
};

// Auto-truncates the given text to fit within the width of the containing element.
// Truncates in the middle, with a placeholder (default: ellipsis).
export const TruncateTextTooltip: React.FC<
  PropsWithChildren<TruncateTextProps>
> = React.memo(
  ({
    fullText,
    placeholderType = 'ellipsis',
    className,
    noLink,
    linkPath,
    truncationType,
    highlight,
    dataTest,
    onClick,
    textAlign,
  }) => {
    const parentDomRef = useRef<HTMLDivElement | null>(null);
    const isTextOverflowing = useIsTextOverflowing({
      text: fullText,
      parentDomRef,
    });
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const styles =
      truncationType === TruncationType.Beginning
        ? truncateBeginningStyle
        : truncationType === TruncationType.End
          ? truncateEndStyle
          : undefined;

    const highlightedText = highlight ? highlight(fullText) : fullText;

    const content =
      truncationType === TruncationType.Middle ? (
        <div
          aria-label={fullText}
          className={isTextOverflowing ? 'isTruncated' : ''}
          style={{...styles, textAlign}}>
          <div aria-hidden className="textStart">
            {highlightedText}
          </div>
          <div
            aria-hidden
            className={classNames('placeholder', placeholderType)}></div>
          <div aria-hidden className="textEnd">
            {highlightedText}
          </div>
        </div>
      ) : (
        <div aria-label={fullText} style={{...styles, textAlign}}>
          {highlightedText}
        </div>
      );

    return (
      <TailwindContents>
        <div
          ref={parentDomRef}
          data-test={dataTest}
          className={
            truncationType === TruncationType.Middle
              ? classNames('TruncateText', className)
              : 'overflow-hidden'
          }>
          <Tooltip.Provider delayDuration={400}>
            <Tooltip.Root
              open={isMenuOpen && isTextOverflowing}
              onOpenChange={setIsMenuOpen}>
              {noLink ? (
                <Tooltip.Trigger asChild onClick={onClick}>
                  <div
                    style={styles}
                    role={onClick || isTextOverflowing ? 'button' : undefined}
                    className={onClick ? 'cursor-pointer' : ''}>
                    {content}
                  </div>
                </Tooltip.Trigger>
              ) : (
                <Tooltip.Trigger asChild>
                  <Link to={linkPath || ''} className="cursor-pointer">
                    {content}
                  </Link>
                </Tooltip.Trigger>
              )}
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  className="max-w-[400px] overflow-visible whitespace-normal break-words"
                  avoidCollisions>
                  {fullText}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      </TailwindContents>
    );
  }
);
